import { useContext, useEffect, useState } from 'react';
import { createColumnHelper } from "@tanstack/react-table";
import TanStackTable from "../../../components/Table/TanStackTable/TanStackTable";
import axios from "axios";
import ContentCreator from "../../../components/ContentCreator/ContentCreator";
import AuthContext from "../../../contexts/AuthContext";
import "./SalesBulkMessages.css";
import showNotification from "../../../utils/showNotification";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import SalesBulkMessagesPreview from "./SalesBulkMessagesPreview/SalesBulkMessagesPreview";
// import SalesBulkMessagesForm from "./SalesBulkMessagesForm/SalesBulkMessagesForm";
import { Messages } from './Messages';

const INITIAL_FORM_VALUES = {
    phones: [],
    mediaData: null,
    message: null,
    messageType: null,
    batch_size: 50,
    api_instances: [],
    interval_range: {
        min_ms: 1500,
        max_ms: 5000,
    },
    pause: 3
}

const validation = {
    phones: (value) => {
        if (!value || value?.length === 0) return 'Debes incluir al menos un número telefónico.'
    },
    batch_size: (value) => {
        if (!value) return 'Ingresa un número que indique la cantidad del bloque de mensajes.'
        if (value > 100) return 'El bloque debe ser un número entre 20 a 100.'
        if (value < 20) return 'El bloque debe ser un número entre 20 a 100.'
    },
    api_instances: (value) => {
        if (!value || value?.length === 0) return 'Debes incluir al menos un bot.'
    },
    'interval_range.min_ms': (value) => {
        if (!value) return 'Ingresa un número que indique el tiempo mínimo entre mensajes.'
        if (value > 18000) return 'El mínimo debe ser un número entre 1500 a 18000.'
        if (value < 1500) return 'El mínimo debe ser un número entre 1500 a 18000.'
    },
    'interval_range.max_ms': (value, formValues) => {
        if (!value) return 'Ingresa un número que indique el tiempo máximo entre mensajes.'
        if (value > 30000) return 'El máximo debe ser un número entre 4000 a 30000.'
        if (value < 4000) return 'El máximo debe ser un número entre 4000 a 30000.'

        // 👇 Validación adicional
        if (formValues?.interval_range?.min_ms && value <= formValues.interval_range.min_ms) {
            return 'El máximo debe ser mayor que el mínimo.'
        }
    },
    pause: (value) => {
        if (!value) return 'Ingresa un número que indique el tiempo de pausa entre cada bloque.'
        if (value > 10) return 'La pausa debe ser número entre 3 a 10.'
        if (value < 3) return 'La pausa debe ser número entre 3 a 10.'
    },
}


function SalesBulkMessages() {
    const { authToken } = useContext(AuthContext);

    const [campaignsData, setCampaignsData] = useState([]);
    // In this case, initial values will be used to replicate a campaign, not for editing
    const [initialCampaignValues, setInitialCampaignValues] = useState(null);
    const [displayForm, setDisplayForm] = useState(false);
    const [updateData, setUpdateData] = useState(false);
    const [loading, setLoading] = useState(false);

    const axiosConfig = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
        }
    };

    const createCampaign = async (values, config) => {
        const message = values.message !== null ? values.message : values.mediaData
        const jsonData = {
            message,
            phones: values.phones,
            config: {
                message_type: values.mediaData !== null ? 'media' : 'text',
                batch_size: values.batch_size,
                min_ms: values.interval_range.min_ms,
                max_ms: values.interval_range.max_ms,
                pause: values.pause,
                api_instances: values.api_instances
            }
        }

        return axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/createMessageCampaign`,
            { data: jsonData },
            config
        );
    };

    // COLUMN DEFINITIONS
    const columnHelper = createColumnHelper();
    const columns = [

    ];

    // TABLE FUNCTIONS
    const handleSubmit = async (values) => {

        if (!values.api_instances || values.api_instances?.length === 0) {
            return showNotification({
                color: "red",
                status: "error",
                title: "Operación cancelada",
                message: `No se han encontrado bots para realizar los mensajes.`,
            });
        }

        if (!values.phones || values.phones?.length === 0) {
            return showNotification({
                color: "red",
                status: "error",
                title: "Operación cancelada",
                message: `No se han encontrado números de telefónicos.`,
            });
        }

        if (!values?.message && !values?.mediaData) {
            return showNotification({
                color: "red",
                status: "error",
                title: "Operación cancelada",
                message: `No hay mensaje confirmado a utilizar.`,
            });
        }

        try {
            setLoading(true);
            await createCampaign(values, axiosConfig);

            showNotification({
                color: "green",
                status: "success",
                title: "¡Excelente! Operación exitosa.",
                message: `La campaña ha sido creada / iniciada con éxito.`,
            });
            setUpdateData(!updateData);
            setDisplayForm(false);
        } catch (err) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo crear / iniciar la campaña. Detalle: ${err.message || ERROR_HANDLER[err.request?.status] || ERROR_HANDLER.defaultError
                    }`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
            setCampaignsData(null);
        }
    };

    // DATA INITIALIZATION
    const init = async () => {
        // Get campaigns
        // const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/campaigns`;
        // const campaignsInfo = await axios.get(url, axiosConfig);

        setCampaignsData([]);
    };

    useEffect(() => {
        init();
    }, [updateData]);

    return (
        <div className="salesBulkMessages__container">
            {displayForm && (
                <ContentCreator
                    title={"Crear campaña"}
                    submitFunc={(values) => handleSubmit(values)}
                    onCancel={() => {
                        setDisplayForm(false);
                        setInitialCampaignValues(null);
                    }}
                    validation={validation}
                    initialValues={initialCampaignValues ?? INITIAL_FORM_VALUES}
                    renderPreview={(_form) => (
                        <SalesBulkMessagesPreview form={_form.form} />
                    )}
                    renderForm={(_form) => (
                        // <SalesBulkMessagesForm initialValues={campaignsData} loading={loading} form={_form.form}/>
                        <Messages form={_form.form} loading={loading} />
                    )}
                />
            )}
            {campaignsData && (
                <TanStackTable
                    data={campaignsData}
                    columns={columns}
                    // editFunc={handleEditCampaign}
                    displayForm={displayForm}
                    addButtonFunc={() => {
                        setDisplayForm(!displayForm);
                        if (displayForm) setInitialCampaignValues(null);
                    }}
                />
            )}
        </div>
    );
}

export default SalesBulkMessages;
